import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Typography from "@/elements/Typography";
import Card from "@mui/material/Card";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  getAllarmiDispositivo,
  getDispositivoColor,
} from "@/utils/dispositivoUtils";
import { Grid, Tooltip } from "@mui/material";
import DispositivoStatus from "../DispositivoStatus";
import ProprietaMisurataCard from "../ProprietaMisurate/Card/Singole/ProprietaMisurataCard";
import {
  NodoDto,
  ProprietaMisurataDto,
  getGeneraCertificatoMQTTQueryKey,
} from "@/api";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ProprietaMisurataHistoryDialog from "../ProprietaMisurataHistoryDialog";
import TrasferimentiDialog from "../TrasferimentiDialog";
import { useAuth } from "@/context/useAuth";
import ImpostazioniNotificheDialog from "../ImpostazioniNotificheDialog";
import RegistriMqttDialog from "./RegistriMqttDialog";
import ManutenzioniTableCard from "./ManutenzioniTable";
import ProprietaParticelleMisurateCard from "../ProprietaMisurate/Card/Raggruppate/ProprietaParticelleMisurateCard";
import ProprietaRMSMisurateCard from "../ProprietaMisurate/Card/Raggruppate/ProprietaRMSMisurateCard";
import ProprietaFFTMisurateCard from "../ProprietaMisurate/Card/Raggruppate/ProprietaFFTMisurateCard";
import ProprietaResistenzeMisurateCard from "../ProprietaMisurate/Card/Raggruppate/ProprietaResistenzeMisurateCard";
import { splitAndSortProprietaMisurate } from "@/utils/proprietaMisurataUtils";
import DispositivoCSVDialog from "../DispositivoCSVDialog";
import moment from "moment";
import { getMascheraDispositivoHelper } from "@/components/DispositivoDetails/maschere";
import ConnessioniGatewayDialog from "../ConnessioniGatewayDialog";
import { GFL_DFA_3500_Action } from "./maschere/buttons/GFL_DFA_3500_Buttons";
import { BANCO_DI_FLUSSAGGIO_Action } from "./maschere/buttons/BANCO_DI_FLUSSAGGIO_Buttons";
import { GFL_TH_4000_Action } from "./maschere/buttons/GFL_TH_4000_Buttons";
import { GFL_TFLD_550_2_Action } from "./maschere/buttons/GFL_TFLD_550_2_Buttons";
import { InfoContaOre } from "./maschere/info/InfoContaOre";
import NexusAttuatoreCard from "./NexusAttuatoreCard";
import GestisciReminderDialog from "../CentraleDetailsPage/GestisciReminderDialog";

export type DispositivoAction =
  | "start"
  | "start-manuale"
  | "stop"
  | "stop-manuale"
  | GFL_DFA_3500_Action
  | BANCO_DI_FLUSSAGGIO_Action
  | GFL_TH_4000_Action
  | GFL_TFLD_550_2_Action;

function DispositivoDetails({ nodo }: { nodo: NodoDto }) {
  const { t } = useTranslation();
  const { hasPermission, user } = useAuth();

  const modelloObj = nodo.dispositivoModello!;
  const isNexus = modelloObj.modello === "Nexus 6D";

  const allarmiDispositivo = useMemo(() => {
    return getAllarmiDispositivo(nodo);
  }, [nodo]);

  const proprietaMisurate = nodo.dispositivoProprietaMisurate ?? [];

  const {
    proprietaStandardMisurate,
    proprietaISOMisurate,
    proprietaRmsMisurate,
    proprietaFftMisurate: proprietaFftMisurate,
    proprietaNexusPinMisurate,
    proprietaResistenzeMisurate,
  } = useMemo(() => {
    return splitAndSortProprietaMisurate(proprietaMisurate);
  }, [proprietaMisurate]);

  const helper = modelloObj?.maschera
    ? getMascheraDispositivoHelper(modelloObj.maschera)
    : null;
  const ButtonsComponent = helper?.ButtonsComponent;
  const InfoComponent = helper?.InfoComponent ?? InfoContaOre;

  const RegistriDispositivoDialog =
    helper && "DialogComponent" in helper
      ? helper.DialogComponent
      : RegistriMqttDialog;

  const powerStatus =
    !!nodo.dispositivoLastStatus &&
    [
      "on",
      ...(helper?.statusManuale ? helper.statusManuale : ["on-manuale"]),
      "alarm",
    ].includes(nodo.dispositivoLastStatus);
  const manuale =
    !!nodo.dispositivoLastStatus &&
    (helper?.statusManuale
      ? helper.statusManuale
      : ["on-manuale, off-manuale"]
    ).includes(nodo.dispositivoLastStatus);
  const connectionStatus =
    !!nodo.dispositivoLastStatus && nodo.dispositivoLastStatus !== "off";

  const rightsideControls = (
    <Box display="flex" flexDirection="column" gap={2} ml={4}>
      <Button
        size="small"
        variant="contained"
        color="light"
        onClick={() => setTrasferimentiDialogOpen(true)}
      >
        {t("dispositivoDetails.azioni.trasferimenti")}
      </Button>

      {hasPermission("GatewayRead") && (
        <Button
          size="small"
          variant="contained"
          color="light"
          onClick={() => setConnessioniGatewayDialogOpen(true)}
        >
          {t("dispositivoDetails.azioni.gateway")}
        </Button>
      )}

      <Button
        size="small"
        variant="contained"
        color="light"
        onClick={() => setCSVDialogOpen(true)}
      >
        {t("dispositivoDetails.azioni.scaricaDatiGrezzi")}
      </Button>

      {hasPermission("DispositivoRegistriWrite") && (
        <Button
          size="small"
          variant="contained"
          color="light"
          disabled={!connectionStatus && !isNexus}
          onClick={() => setModificaRegistriDialogOpen(true)}
        >
          {t("dispositivoDetails.azioni.modificaRegistri")}
        </Button>
      )}

      {/* <Box display="flex" py={1} mt={3} alignItems="center">
        <Typography variant="button" fontWeight="regular" color="text" mr={2}>
          Notifiche:
        </Typography>
        <Switch checked={true} color="success" />
      </Box> */}

      <Button
        size="small"
        variant="contained"
        color="light"
        onClick={() => setImpostazioniNotificheDialogOpen(true)}
      >
        {t("dispositivoDetails.azioni.impostazioniNotifiche")}
      </Button>

      {nodo.haStoricoManutenzioni &&
        hasPermission("DispositiviUpdateReminders") && (
          <Button
            size="small"
            variant="contained"
            color="light"
            startIcon={<NotificationsIcon />}
            onClick={() => setReminderDialogOpen(true)}
          >
            {t("nodoPage.azioni.gestisciReminder")}
          </Button>
        )}

      {hasPermission("DispositivoGeneraCertificato") && (
        <Button
          size="small"
          variant="contained"
          color="light"
          href={getGeneraCertificatoMQTTQueryKey(nodo.id)[0]}
        >
          {t("dispositivoDetails.azioni.certificatiMqtt")}
        </Button>
      )}
    </Box>
  );

  const trasferimento =
    nodo.breadcrumbs && nodo.breadcrumbs[nodo.breadcrumbs.length - 1];

  const content = (
    <Box flex={1}>
      <Box
        display="flex"
        flexDirection="row"
        gap={2}
        sx={{
          containerType: "inline-size",
        }}
      >
        <Box
          p={2}
          sx={{
            maxWidth: 300,
            "& > svg": {
              width: "100%",
            },
            "@container (max-width: 450px)": {
              display: "none",
            },
            "@container (min-width: 650px)": {
              marginRight: "50px",
            },
          }}
        >
          <img
            width="100%"
            src={modelloObj?.imageUrl ?? "/immagini-dispositivi/default.png"}
            alt={t("dispositivoDetails.immagineAlt")}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap={2} pt={4}>
          <DispositivoStatus
            type="connection"
            status={connectionStatus}
            controls={
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ height: "100%" }}
              >
                {nodo.dispositivoLastStatus !== "off" && (
                  <DispositivoColor nodo={nodo} />
                )}
              </Box>
            }
          />

          {connectionStatus && (
            <DispositivoStatus
              type="power"
              status={powerStatus}
              manuale={manuale}
              alarm={nodo.dispositivoLastStatus === "alarm"}
              controls={
                user?.isEsterno ? null : (
                  <Box display="flex" flexDirection="column" gap={1}>
                    {ButtonsComponent ? <ButtonsComponent nodo={nodo} /> : null}
                  </Box>
                )
              }
            />
          )}

          <Box gap={1}>
            <Box>
              <Typography variant="caption">
                {t("dispositivoDetails.campi.modello")}
              </Typography>
              <Typography variant="h6">{modelloObj?.modello}</Typography>
            </Box>
            <Box>
              <Typography variant="caption">
                {t("dispositivoDetails.campi.matricola")}
              </Typography>
              <Typography variant="h6">{nodo.dispositivoMatricola}</Typography>
            </Box>

            {modelloObj?.tipoDispositivo === "sensore" &&
              nodo.dispositivoLastInfo &&
              nodo.dispositivoLastInfo.fwVersion && (
                <Box>
                  <Typography variant="caption">
                    {t("dispositivoDetails.campi.firmware")}
                  </Typography>
                  <Typography variant="h6">
                    {nodo.dispositivoLastInfo.fwVersion}
                  </Typography>
                </Box>
              )}
          </Box>

          {allarmiDispositivo && allarmiDispositivo.length > 0 && (
            <Box>
              <Typography variant="caption" color="error">
                {allarmiDispositivo.length === 1
                  ? t("dispositivoDetails.allarmi.singolo")
                  : t("dispositivoDetails.allarmi.plurale")}
              </Typography>
              {allarmiDispositivo.map((allarme) => {
                return (
                  <Typography variant="h6" color="error" key={allarme}>
                    {allarme}
                  </Typography>
                );
              })}
            </Box>
          )}

          <InfoComponent nodo={nodo} />
        </Box>
      </Box>
    </Box>
  );

  const [historyDialogOptions, setHistoryDialogOptions] = useState<{
    proprietaMisurate: ProprietaMisurataDto[];
  } | null>(null);

  const [trasferimentiDialogOpen, setTrasferimentiDialogOpen] = useState(false);
  const [connessioniGatewayDialogOpen, setConnessioniGatewayDialogOpen] =
    useState(false);
  const [impostazioniNotificheDialogOpen, setImpostazioniNotificheDialogOpen] =
    useState(false);
  const [modificaRegistriDialogOpen, setModificaRegistriDialogOpen] =
    useState(false);
  const [csvDialogOpen, setCSVDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);

  function openHistoryDialog(proprietaMisurate: ProprietaMisurataDto[]): void {
    setHistoryDialogOptions({ proprietaMisurate });
  }

  return (
    <Box p={2} display="flex" flexDirection="column" gap={2}>
      <Grid container>
        <Grid item xs={12}>
          <Card>
            <Box p={2}>
              <Box display="flex" flexDirection="row" alignItems="flex-start">
                {content}
                {rightsideControls}
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Box
            mt={2}
            mb={0}
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            gap={2}
          >
            {proprietaRmsMisurate.length > 0 && (
              <ProprietaRMSMisurateCard
                dispositivo={nodo}
                proprietaMisurate={proprietaRmsMisurate}
                openHistoryDialog={() =>
                  openHistoryDialog(proprietaRmsMisurate)
                }
              />
            )}
            {proprietaFftMisurate.length > 0 && (
              <ProprietaFFTMisurateCard
                dispositivo={nodo}
                proprietaMisurate={proprietaFftMisurate}
                openHistoryDialog={() =>
                  openHistoryDialog(proprietaFftMisurate)
                }
              />
            )}
            {proprietaResistenzeMisurate.length > 0 && (
              <ProprietaResistenzeMisurateCard
                dispositivo={nodo}
                proprietaMisurate={proprietaResistenzeMisurate}
                openHistoryDialog={() =>
                  openHistoryDialog(proprietaResistenzeMisurate)
                }
              />
            )}
            {proprietaStandardMisurate.map((proprietaMisurata, index) => {
              return (
                <ProprietaMisurataCard
                  key={index}
                  dispositivo={nodo}
                  proprietaMisurata={proprietaMisurata}
                  proprietaMisurataFft={proprietaFftMisurate}
                  value={proprietaMisurata.lastValue ?? null}
                  openHistoryDialog={() =>
                    openHistoryDialog([proprietaMisurata])
                  }
                />
              );
            })}
            {proprietaISOMisurate.length > 0 && (
              <ProprietaParticelleMisurateCard
                dispositivo={nodo}
                proprietaMisurate={proprietaISOMisurate}
                openHistoryDialog={() =>
                  openHistoryDialog(proprietaISOMisurate)
                }
              />
            )}
            {nodo.dispositivoNexusAttuatori &&
              nodo.dispositivoNexusAttuatori.map((attuatore) => {
                return (
                  <NexusAttuatoreCard
                    key={attuatore.id}
                    attuatore={attuatore}
                    proprietaNexusPinMisurate={proprietaNexusPinMisurate}
                  />
                );
              })}
          </Box>
        </Grid>
      </Grid>

      <ProprietaMisurataHistoryDialog
        open={!!historyDialogOptions}
        dispositivo={nodo}
        proprietaMisurateInitial={historyDialogOptions?.proprietaMisurate}
        onClose={() => setHistoryDialogOptions(null)}
      />
      <TrasferimentiDialog
        open={trasferimentiDialogOpen}
        nodo={nodo}
        onClose={() => setTrasferimentiDialogOpen(false)}
        canEdit={hasPermission("TrasferimentiChange")}
      />
      <ConnessioniGatewayDialog
        open={connessioniGatewayDialogOpen}
        nodo={nodo}
        onClose={() => setConnessioniGatewayDialogOpen(false)}
        canEdit={hasPermission("ConnessioneGatewayChange")}
      />
      <ImpostazioniNotificheDialog
        open={impostazioniNotificheDialogOpen}
        dispositivo={nodo}
        proprietaMisurate={nodo.dispositivoProprietaMisurate ?? undefined}
        onClose={() => setImpostazioniNotificheDialogOpen(false)}
      />
      <RegistriDispositivoDialog
        open={modificaRegistriDialogOpen}
        dispositivo={nodo}
        onClose={() => setModificaRegistriDialogOpen(false)}
      />
      <DispositivoCSVDialog
        open={csvDialogOpen}
        onClose={() => setCSVDialogOpen(false)}
        dispositivo={nodo}
      />
      <GestisciReminderDialog
        nodo={nodo}
        open={reminderDialogOpen}
        onClose={() => setReminderDialogOpen(false)}
      />
      {nodo.haStoricoManutenzioni &&
        hasPermission("DispositiviReadManutenzioni") && (
        <Grid xs={12}>
          <ManutenzioniTableCard
            nodo={nodo}
            canEdit={hasPermission("DispositiviUpdateManutenzioni")}
          />
        </Grid>
        )}
    </Box>
  );
}

export default DispositivoDetails;

export function DispositivoColor({ nodo }: { nodo: NodoDto }) {
  const { t } = useTranslation();

  const title = useMemo(() => {
    let title = t("dispositivoDetails.led.colore");

    if (nodo.dispositivoLastColorDate) {
      const m = moment(nodo.dispositivoLastColorDate);
      if (m.isValid()) {
        title = t("dispositivoDetails.led.coloreAggiornato", {
          data: m.format("DD/MM/YYYY"),
          ora: m.format("HH:mm"),
        });
      }
    }

    return title;
  }, [nodo, t]);

  const color = getDispositivoColor(nodo, { sourceLastColor: true });

  if (!color) {
    return null;
  }

  // If no icon, render a fallback Box with the color
  const content = color.icon ?? (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: color.color,
      }}
    />
  );

  return (
    <Tooltip title={title}>
      <Box component="span" sx={{ width: 20, height: 20 }}>
        {content}
      </Box>
    </Tooltip>
  );
}
